import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import Grid from "@mui/material/Grid2";
import { useAuth } from "../../../contexts/AuthContext";
import DashboardV2 from "./DashboardV2";
import DashboardInterpretationsMonthly from "./DashboardInterpretationsMonthly";
import DashboardAllGeneral from "./DashboardAllGeneral";
import DashboardTranslationsMonthly from "./DashboardTranslationsMonthly";
import DashboardOtherServicesMonthly from "./DashboardOtherServicesMonthly";
import dayjs from "dayjs";
import DashboardInterpretationsWeekly from "./DashboardInterpretationsWeekly";
import DashboardTranslationsWeekly from "./DashboardTranslationsWeekly";
import DashboardOtherServicesWeekly from "./DashboardOtherServicesWeekly";
import DashboardAllServices from "./DashboardAllServices";
import DashboardAllClients from "./DashboardAllClients";
import DashboardAllLinguists from "./DashboardAllLinguists";
import api from "services/api";
import { NAVBAR_HEIGHT } from "constants/layout";
import DashboardFilterSelector from "components/DashboardFilterSelector";
import {
  FILTER_CONTAINER_GRID,
  FILTER_SELECTOR_WIDTH,
} from "constants/filterSelectors";

const DASHBOARD_MODE_V2_KEY = "DashboardModeV2Enabled";

const Dashboard = () => {
  usePageTitle("Dashboard");
  const { currentUser } = useAuth();
  const theme = useTheme();
  const { refreshPage } = useStateContext();

  // State variables
  const [, setIsLoading] = useState(true);
  const [, setMinMaxDates] = useState();

  // Current year for defaults
  const currentYear = dayjs().year();
  const currentMonth = dayjs().format("MMM");

  // Define months array
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Initialize available years with empty array - will be populated from API
  const [availableYears, setAvailableYears] = useState([]);

  // Replace date state with dateSelection state
  const [dateSelection, setDateSelection] = useState({
    months: [currentMonth], // Current month
    years: [currentYear], // Current year
  });

  const [selectedView, setSelectedView] = useState(
    window.localStorage.getItem("DashboardView") || "Monthly",
  );
  const [selectedOverview, setSelectedOverview] = useState(
    window.localStorage.getItem("DashboardOverviewTab") || "General Overview",
  );
  const [selectedPage, setSelectedPage] = useState(
    Number(window.localStorage.getItem("DashboardTab") || 0),
  );
  const [selectedViewV2, setSelectedViewV2] = useState(
    window.localStorage.getItem("DashboardV2View") || "Monthly",
  );
  const [selectedOverviewV2, setSelectedOverviewV2] = useState(
    window.localStorage.getItem("DashboardV2OverviewTab") ||
      "General Overview",
  );
  const [selectedPageV2, setSelectedPageV2] = useState(
    Number(window.localStorage.getItem("DashboardV2Tab") || 0),
  );
  const [isDashboardV2, setIsDashboardV2] = useState(
    // V2 is the default dashboard; the toggle still allows opting back to V1
    window.localStorage.getItem(DASHBOARD_MODE_V2_KEY) !== "0",
  );
  const { handleErrorDialog } = useStateContext();

  // Constants
  //TO DO Limit 'ALL' only to Admin
  const dashboardPagesTitles =
    currentUser.Role === "Admin"
      ? ["Interpretations", "Translations", "Other Services", "ALL"]
      : ["Interpretations", "Translations", "Other Services"];
  const dashboardPagesViews = ["Weekly", "Monthly"];
  const dashboardOverviews = [
    "General Overview",
    //"Finances",
    "Services",
    "Clients",
    "Linguists",
  ];

  const activeSelectedPage = isDashboardV2 ? selectedPageV2 : selectedPage;
  const activeSelectedView = isDashboardV2 ? selectedViewV2 : selectedView;
  const activeSelectedOverview = isDashboardV2
    ? selectedOverviewV2
    : selectedOverview;

  // Helper functions for layout calculations
  const isAllServiceSelected = () => {
    const allIndex = dashboardPagesTitles.indexOf("ALL");
    return selectedPage === allIndex;
  };
  const isWeeklyView = () => selectedView === "Weekly";

  const handleDashboardModeToggle = (event) => {
    const checked = event.target.checked;
    setIsDashboardV2(checked);
    window.localStorage.setItem(DASHBOARD_MODE_V2_KEY, checked ? "1" : "0");
  };
  
  const handleDateSelectionSave = (newSelection) => {
    // Ensure only one year is selected
    const updatedSelection = {
      months: newSelection.months,
      years: newSelection.years.slice(-1), // Take only the last selected year
    };

    setDateSelection(updatedSelection);
  };

  const dashboardAllViews = () => {
    switch (selectedOverview) {
      case "General Overview":
        return (
          <DashboardAllGeneral
            user={currentUser}
            dateSelection={dateSelection}
            key="page-all-general"
          />
        );
      case "Services":
        return (
          <DashboardAllServices
            user={currentUser}
            dateSelection={dateSelection}
            key="page-all-services"
          />
        );
      case "Clients":
        return (
          <DashboardAllClients
            user={currentUser}
            dateSelection={dateSelection}
            key="page-all-clients"
          />
        );
      case "Linguists":
        return (
          <DashboardAllLinguists
            user={currentUser}
            dateSelection={dateSelection}
            key="page-all-linguists"
          />
        );
      default:
        return (
          <DashboardAllGeneral
            user={currentUser}
            dateSelection={dateSelection}
            key="page-all-general"
          />
        );
    }
  };

  const dashboardPagesMonthly = [
    <DashboardInterpretationsMonthly
      user={currentUser}
      dateSelection={dateSelection}
      key="page-interpretations"
    />,
    <DashboardTranslationsMonthly
      user={currentUser}
      dateSelection={dateSelection}
      key="page-translations"
    />,
    <DashboardOtherServicesMonthly
      user={currentUser}
      dateSelection={dateSelection}
      key="page-others"
    />,
    null, // ALL option (now at the end)
  ];

  const dashboardPagesWeekly = [
    <DashboardInterpretationsWeekly
      user={currentUser}
      key="page-interpretations"
    />,
    <DashboardTranslationsWeekly user={currentUser} key="page-translations" />,
    <DashboardOtherServicesWeekly user={currentUser} key="page-others" />,
    <DashboardAllGeneral
      user={currentUser}
      dateSelection={dateSelection}
      key="page-all"
    />, // ALL option (now at the end)
  ];

  const getPage = () => {
    if (isAllServiceSelected()) return dashboardAllViews();

    return isWeeklyView()
      ? dashboardPagesWeekly[selectedPage]
      : dashboardPagesMonthly[selectedPage];
  };

  const dateSelectionYearsRef = useRef(dateSelection.years);
  dateSelectionYearsRef.current = dateSelection.years;

  // Process date range data from API
  const processDateRangeData = useCallback((dateRangeData) => {
    if (!dateRangeData) {
      console.warn("Invalid date range data received");
      return [currentYear];
    }

    // Check if the API returns data in the format with startDate/endDate properties
    if (dateRangeData.startDate && dateRangeData.endDate) {
      const startYear =
        dateRangeData.startDate.year || dayjs(dateRangeData.startDate).year();
      const endYear =
        dateRangeData.endDate.year || dayjs(dateRangeData.endDate).year();

      // Create array of years from min to max in ascending order (smaller to bigger)
      const years = [];
      for (let year = startYear; year <= endYear; year++) {
        years.push(year);
      }

      return years;
    }

    // Check if the API returns data in the format with minDate/maxDate properties
    if (dateRangeData.minDate && dateRangeData.maxDate) {
      // Try to extract year directly if it's an object with year property
      let minYear, maxYear;

      if (
        typeof dateRangeData.minDate === "object" &&
        dateRangeData.minDate.year
      ) {
        minYear = dateRangeData.minDate.year;
      } else {
        minYear = dayjs(dateRangeData.minDate).year();
      }

      if (
        typeof dateRangeData.maxDate === "object" &&
        dateRangeData.maxDate.year
      ) {
        maxYear = dateRangeData.maxDate.year;
      } else {
        maxYear = dayjs(dateRangeData.maxDate).year();
      }

      // Create array of years from min to max in ascending order (smaller to bigger)
      const years = [];
      for (let year = minYear; year <= maxYear; year++) {
        years.push(year);
      }

      return years;
    }

    // Fallback to current year if no valid date range found
    console.warn("No valid date range format found in API response");
    return [currentYear];
  }, [currentYear]);

  useEffect(() => {
    setIsLoading(true);

    api
      .get("/dashboard/serviceDateRange")
      .then((response) => {
        if (response && response.data) {
          const dateRangeData = response.data;
          setMinMaxDates(dateRangeData);

          // Process years from the date range without sorting
          const years = processDateRangeData(dateRangeData);

          if (years.length > 0) {
            setAvailableYears(years);

            // Update dateSelection if the current year isn't in the range
            const currentSelectedYear = dateSelectionYearsRef.current[0];
            if (!years.includes(currentSelectedYear)) {
              // Choose the closest year in range to the current year
              let newYear;
              if (years.includes(currentYear)) {
                newYear = currentYear;
              } else if (currentYear < years[0]) {
                newYear = years[0]; // Smallest year
              } else {
                newYear = years[years.length - 1]; // Largest year
              }

              setDateSelection((prev) => ({
                ...prev,
                years: [newYear],
              }));
            }
          } else {
            // If no valid years, set fallback
            setAvailableYears([currentYear]);
          }
        } else {
          console.warn("Invalid or empty response from API");
          setAvailableYears([currentYear]);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching date range:", error);
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";

        handleErrorDialog(message, theme);

        // Set fallback years on error
        setAvailableYears([currentYear]);
        setIsLoading(false);
      });
  }, [refreshPage, currentYear, handleErrorDialog, processDateRangeData, theme]);

  return (
    <Box
      //borderRadius="1.5rem"
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`, // Account for navbar + bottom bar in layout
      }}
    >
      <Box>
        <Grid container alignItems="center" justifyContent="flex-start">
          <Grid
            size={{ xs: 12 }}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              px: 1,
              mb: 1,
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ width: "100%", maxWidth: FILTER_SELECTOR_WIDTH }}
            >
              <Typography
                variant="body2"
                sx={{ color: theme.palette.primary.text2, fontWeight: 600 }}
              >
                Legacy
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={isDashboardV2}
                    onChange={handleDashboardModeToggle}
                    size="medium"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.primary.text2, fontWeight: 700 }}
                  >
                    Dashboard V2
                  </Typography>
                }
                sx={{ ml: 0 }}
              />
            </Stack>
          </Grid>
          <Grid
            size={FILTER_CONTAINER_GRID}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              px: 1,
            }}
          >
            <Box sx={{ width: "100%", maxWidth: FILTER_SELECTOR_WIDTH }}>
              <DashboardFilterSelector
                selectedPage={activeSelectedPage}
                onPageChange={(newIndex) => {
                  if (isDashboardV2) {
                    setSelectedPageV2(newIndex);
                    window.localStorage.setItem("DashboardV2Tab", Number(newIndex));
                    return;
                  }

                  setSelectedPage(newIndex);
                  window.localStorage.setItem("DashboardTab", Number(newIndex));
                }}
                dashboardPagesTitles={dashboardPagesTitles}
                selectedView={activeSelectedView}
                onViewChange={(newView) => {
                  if (isDashboardV2) {
                    setSelectedViewV2(newView);
                    window.localStorage.setItem("DashboardV2View", newView);
                    return;
                  }

                  setSelectedView(newView);
                  window.localStorage.setItem("DashboardView", newView);
                }}
                dashboardPagesViews={dashboardPagesViews}
                selectedOverview={activeSelectedOverview}
                onOverviewChange={(newOverview) => {
                  if (isDashboardV2) {
                    setSelectedOverviewV2(newOverview);
                    window.localStorage.setItem(
                      "DashboardV2OverviewTab",
                      newOverview,
                    );
                    return;
                  }

                  setSelectedOverview(newOverview);
                  window.localStorage.setItem(
                    "DashboardOverviewTab",
                    newOverview,
                  );
                }}
                dashboardOverviews={dashboardOverviews}
                dateSelection={dateSelection}
                onDateSelectionSave={handleDateSelectionSave}
                months={months}
                years={availableYears}
                currentYear={currentYear}
                isAllServiceSelected={(page) => {
                  const allIndex = dashboardPagesTitles.indexOf("ALL");
                  return page === allIndex;
                }}
                shouldShowDatePicker={(page, view, overview) => {
                  const allIndex = dashboardPagesTitles.indexOf("ALL");
                  const isAll = page === allIndex;
                  const isGeneral = overview === "General Overview";
                  const isServices = overview === "Services";
                  const isWeekly = view === "Weekly";
                  return (
                    (isAll && (isGeneral || isServices)) ||
                    (!isAll && !isWeekly)
                  );
                }}
                shouldAllowMultiMonth={(page) => {
                  const allIndex = dashboardPagesTitles.indexOf("ALL");
                  return page === allIndex;
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box
        //borderRadius="1.5rem"
        bgcolor={theme.palette.background.alt}
        //p="1rem .5rem 0 .5rem"
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          marginTop: "0.5rem",
        }}
      >
        {isDashboardV2 ? (
          <DashboardV2
            user={currentUser}
            dateSelection={dateSelection}
            selectedPage={selectedPageV2}
            selectedView={selectedViewV2}
            selectedOverview={selectedOverviewV2}
            dashboardPagesTitles={dashboardPagesTitles}
          />
        ) : (
          getPage()
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;
